import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  personnelAssignmentHistory: {},
  loading: false,
  error: null,
};

const FETCH_ASSIGNMENT_HISTORY_REQUEST = "FETCH_ASSIGNMENT_HISTORY_REQUEST";
const FETCH_ASSIGNMENT_HISTORY_SUCCESS = "FETCH_ASSIGNMENT_HISTORY_SUCCESS";
const FETCH_ASSIGNMENT_HISTORY_FAILURE = "FETCH_ASSIGNMENT_HISTORY_FAILURE";
const CREATE_ASSIGNMENT_HISTORY_REQUEST = "CREATE_ASSIGNMENT_HISTORY_REQUEST";
const CREATE_ASSIGNMENT_HISTORY_SUCCESS = "CREATE_ASSIGNMENT_HISTORY_SUCCESS";
const CREATE_ASSIGNMENT_HISTORY_FAILURE = "CREATE_ASSIGNMENT_HISTORY_FAILURE";
const UPDATE_ASSIGNMENT_HISTORY_REQUEST = "UPDATE_ASSIGNMENT_HISTORY_REQUEST";
const UPDATE_ASSIGNMENT_HISTORY_SUCCESS = "UPDATE_ASSIGNMENT_HISTORY_SUCCESS";
const UPDATE_ASSIGNMENT_HISTORY_FAILURE = "UPDATE_ASSIGNMENT_HISTORY_FAILURE";
const DELETE_ASSIGNMENT_HISTORY_REQUEST = "DELETE_ASSIGNMENT_HISTORY_REQUEST";
const DELETE_ASSIGNMENT_HISTORY_SUCCESS = "DELETE_ASSIGNMENT_HISTORY_SUCCESS";
const DELETE_ASSIGNMENT_HISTORY_FAILURE = "DELETE_ASSIGNMENT_HISTORY_FAILURE";

export const getPersonnelAssignmentHistory =
  (personnelId, sortField = "sectionDate", sortDirection = "desc") =>
  async (dispatch) => {
    const service = getService(serviceNames.perAssignmentHistory);
    const query = {
      personnelId,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      isDeleted: false,
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_ASSIGNMENT_HISTORY_REQUEST,
        success: FETCH_ASSIGNMENT_HISTORY_SUCCESS,
        failure: FETCH_ASSIGNMENT_HISTORY_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (result) => ({
        personnelId,
        personnelAssignmentHistory: result,
      }),
      errorMessagePrefix: "Failed to fetch personnel assignment history",
    });
  };

export const createPersonnelAssignmentHistory =
  (assignmentHistoryData) => async (dispatch) => {
    const service = getService(serviceNames.perAssignmentHistory);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: CREATE_ASSIGNMENT_HISTORY_REQUEST,
        success: CREATE_ASSIGNMENT_HISTORY_SUCCESS,
        failure: CREATE_ASSIGNMENT_HISTORY_FAILURE,
      },
      apiCall: () => service.create(assignmentHistoryData),
      transformResult: (result) => ({
        ...result,
        personnelId: assignmentHistoryData.personnelId,
      }),
      successMessage: "Personnel assignment history created successfully",
      errorMessagePrefix: "Failed to create personnel assignment history",
    });
  };

export const updatePersonnelAssignmentHistory =
  (id, assignmentHistoryData) => async (dispatch) => {
    const service = getService(serviceNames.perAssignmentHistory);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_ASSIGNMENT_HISTORY_REQUEST,
        success: UPDATE_ASSIGNMENT_HISTORY_SUCCESS,
        failure: UPDATE_ASSIGNMENT_HISTORY_FAILURE,
      },
      apiCall: () => service.patch(id, assignmentHistoryData),
      transformResult: (result) => ({
        ...result,
        personnelId: assignmentHistoryData.personnelId,
        id: id,
      }),
      successMessage: "Personnel assignment history updated successfully",
      errorMessagePrefix: "Failed to update personnel assignment history",
    });
  };

export const deletePersonnelAssignmentHistory =
  (id, userId, personnelId) => async (dispatch) => {
    const service = getService(serviceNames.perAssignmentHistory);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedAt: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: DELETE_ASSIGNMENT_HISTORY_REQUEST,
        success: DELETE_ASSIGNMENT_HISTORY_SUCCESS,
        failure: DELETE_ASSIGNMENT_HISTORY_FAILURE,
      },
      apiCall: () => service.patch(id, patchData),
      transformResult: () => ({ id, personnelId }),
      successMessage: "Assignment history deleted successfully",
      errorMessagePrefix: "Failed to delete assignment history",
    });
  };

export default function personnelAssignmentHistoryReducer(
  state = initialState,
  action
) {
  switch (action.type) {
    case FETCH_ASSIGNMENT_HISTORY_REQUEST:
    case CREATE_ASSIGNMENT_HISTORY_REQUEST:
    case UPDATE_ASSIGNMENT_HISTORY_REQUEST:
    case DELETE_ASSIGNMENT_HISTORY_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_ASSIGNMENT_HISTORY_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelAssignmentHistory: {
          ...state.personnelAssignmentHistory,
          [action.payload.personnelId]:
            action.payload.personnelAssignmentHistory,
        },
      };
    case CREATE_ASSIGNMENT_HISTORY_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelAssignmentHistory: {
          ...state.personnelAssignmentHistory,
          [action.payload.personnelId]: [
            ...(state.personnelAssignmentHistory[action.payload.personnelId] ||
              []),
            action.payload,
          ],
        },
      };
    case UPDATE_ASSIGNMENT_HISTORY_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelAssignmentHistory: {
          ...state.personnelAssignmentHistory,
          [action.payload.personnelId]: state.personnelAssignmentHistory[
            action.payload.personnelId
          ].map((ec) => (ec.id === action.payload.id ? action.payload : ec)),
        },
      };
    case DELETE_ASSIGNMENT_HISTORY_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelAssignmentHistory: {
          ...state.personnelAssignmentHistory,
          [action.payload.personnelId]: state.personnelAssignmentHistory[
            action.payload.personnelId
          ].filter((ec) => ec.id !== action.payload.id),
        },
      };
    case FETCH_ASSIGNMENT_HISTORY_FAILURE:
    case CREATE_ASSIGNMENT_HISTORY_FAILURE:
    case UPDATE_ASSIGNMENT_HISTORY_FAILURE:
    case DELETE_ASSIGNMENT_HISTORY_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
